import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Collapse from "@mui/material/Collapse";
import Highlighter from "react-highlight-words";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LinkIcon from "@mui/icons-material/Link";
import CheckIcon from "@mui/icons-material/Check";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import { EmptyState } from "../UI/States";
import { faqs, faqCategories } from "./faqData";

const PAGE_SIZE = 8;

// Query param that deep-links a single answer, e.g. /help?a=mfa-setup.
// Keep the name short — these links get pasted into chats and tickets.
const TARGET_PARAM = "a";

// Every word the user types must appear somewhere in the entry (AND, not OR).
// Anything else turns a two-word query like "web lead" into a match on every
// entry that merely mentions "web".
const matchesQuery = (entry, terms) =>
  terms.every((term) => entry.haystack.includes(term));

// Precomputed once at module load: the searchable text for each entry, so
// typing doesn't re-flatten the whole answer body on every keystroke.
const indexed = faqs.map((faq) => ({
  ...faq,
  haystack: [
    faq.question,
    faq.category,
    ...(faq.answer || []),
    ...(faq.steps || []),
    ...(faq.keywords || []),
    faq.link?.label || "",
  ]
    .join(" ")
    .toLowerCase(),
}));

const highlightClass = "bg-amber-200 text-slate-900 rounded px-0.5";

const FaqItem = ({ faq, terms, isOpen, isLinked, onToggle, onCopyLink, copied }) => (
  <div
    id={`faq-${faq.id}`}
    className={
      "app-divider scroll-mt-24 border-b last:border-b-0 " +
      (isLinked ? "bg-sky-500/10" : "")
    }
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="flex min-h-[3.5rem] w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-[color:var(--app-hover)] sm:px-5"
    >
      <span className="flex min-w-0 flex-col">
        <span className="font-semibold leading-snug">
          <Highlighter
            searchWords={terms}
            textToHighlight={faq.question}
            autoEscape
            highlightClassName={highlightClass}
          />
        </span>
        <span className="app-eyebrow mt-1">{faq.category}</span>
      </span>
      <span
        className={`app-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
          isOpen ? "bg-[color:var(--app-hover)]" : ""
        }`}
      >
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </span>
    </button>

    <Collapse in={isOpen} timeout="auto" unmountOnExit>
      <div className="space-y-3 px-4 pb-5 text-[0.95rem] leading-relaxed text-slate-700 dark:text-slate-300 sm:px-5">
        {(faq.answer || []).map((paragraph, i) => (
          <p key={i}>
            <Highlighter
              searchWords={terms}
              textToHighlight={paragraph}
              autoEscape
              highlightClassName={highlightClass}
            />
          </p>
        ))}

        {faq.steps && (
          <ol className="ml-5 list-decimal space-y-1.5 marker:font-semibold marker:text-sky-600 dark:marker:text-sky-300">
            {faq.steps.map((step, i) => (
              <li key={i}>
                <Highlighter
                  searchWords={terms}
                  textToHighlight={step}
                  autoEscape
                  highlightClassName={highlightClass}
                />
              </li>
            ))}
          </ol>
        )}

        {faq.note && (
          <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
            {faq.note}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {faq.link && (
            <Link
              to={faq.link.to}
              className="app-btn app-btn-primary app-btn-sm"
            >
              {faq.link.label}
              <ArrowForwardIcon fontSize="small" />
            </Link>
          )}

          <button
            type="button"
            onClick={onCopyLink}
            className="app-btn app-btn-secondary app-btn-sm"
          >
            {copied ? (
              <>
                <CheckIcon fontSize="small" />
                Link copied
              </>
            ) : (
              <>
                <LinkIcon fontSize="small" />
                Copy link to this answer
              </>
            )}
          </button>
        </div>
      </div>
    </Collapse>
  </div>
);

const FaqSearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const targetId = searchParams.get(TARGET_PARAM);

  // ?q= pre-fills the search — the header's quick-jump palette (⌘K) links
  // here with whatever the user typed when no page matched.
  const [query, setQuery] = useState(() => searchParams.get("q") || "");
  const [category, setCategory] = useState("All");
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState(null);
  // Ids the user has clicked. This is a *flip* against the default open state,
  // not an open-list — otherwise clicking an auto-expanded result does nothing,
  // since the auto-expand would keep forcing it back open.
  const [flipped, setFlipped] = useState([]);

  const listRef = useRef(null);
  const jumpedFor = useRef(null);
  const copyTimer = useRef(null);

  const terms = useMemo(
    () => query.trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query]
  );

  const results = useMemo(() => {
    return indexed.filter((faq) => {
      if (category !== "All" && faq.category !== category) return false;
      if (!terms.length) return true;
      return matchesQuery(faq, terms);
    });
  }, [terms, category]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  // Clamped rather than stored: a filter change can shrink the list under the
  // current page, and rendering an empty page reads as "no answers".
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  // A shared link has to land on the answer even when it isn't on page 1, so
  // resolve the target's page before the first paint the user sees.
  useEffect(() => {
    if (!targetId || jumpedFor.current === targetId) return;
    const index = results.findIndex((faq) => faq.id === targetId);
    if (index === -1) return;
    jumpedFor.current = targetId;
    setPage(Math.floor(index / PAGE_SIZE) + 1);
  }, [targetId, results]);

  useEffect(() => {
    if (!targetId || jumpedFor.current !== targetId) return;
    document
      .getElementById(`faq-${targetId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [targetId, safePage]);

  useEffect(() => {
    const timer = copyTimer;
    return () => clearTimeout(timer.current);
  }, []);

  // Once the user searches or filters, the deep link is no longer what they're
  // looking at — drop it so it stops force-opening an unrelated answer.
  const clearTarget = () => {
    if (!targetId) return;
    const next = new URLSearchParams(searchParams);
    next.delete(TARGET_PARAM);
    setSearchParams(next, { replace: true });
    jumpedFor.current = null;
  };

  // Changing the search starts a fresh set of results, so previous manual
  // opens/closes no longer mean anything and would fight the auto-expand.
  const search = (value) => {
    setQuery(value);
    setFlipped([]);
    setPage(1);
    clearTarget();
  };

  const filterBy = (cat) => {
    setCategory(cat);
    setPage(1);
    clearTarget();
  };

  const goToPage = (next) => {
    setPage(next);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggle = (id) =>
    setFlipped((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const copyLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}?${TARGET_PARAM}=${id}`;
    // Older browsers on the yard tablets don't expose the async clipboard API,
    // so show the URL rather than failing silently.
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => {
          setCopiedId(id);
          clearTimeout(copyTimer.current);
          copyTimer.current = setTimeout(() => setCopiedId(null), 2000);
        },
        () => window.prompt("Copy this link:", url)
      );
    } else {
      window.prompt("Copy this link:", url);
    }
  };

  // A search narrow enough to be readable is auto-expanded — scanning three
  // collapsed headers to find the answer you already searched for is busywork.
  const autoOpen = terms.length > 0 && results.length <= 3;
  const defaultOpen = (id) => autoOpen || id === targetId;
  const isOpen = (id) =>
    flipped.includes(id) ? !defaultOpen(id) : defaultOpen(id);

  return (
    <div>
      <div className="relative">
        <SearchIcon className="app-muted pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          type="search"
          value={query}
          onChange={(e) => search(e.target.value)}
          placeholder="Search the FAQ — try “web lead”, “forklift”, “dark mode”…"
          aria-label="Search frequently asked questions"
          className="app-input h-14 w-full rounded-2xl border pl-12 pr-12 text-[0.95rem] shadow-sm"
        />
        {query && (
          <button
            type="button"
            onClick={() => search("")}
            aria-label="Clear search"
            className="app-icon-btn absolute right-2 top-2 h-10 w-10"
          >
            <CloseIcon fontSize="small" />
          </button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {["All", ...faqCategories].map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => filterBy(cat)}
            aria-pressed={category === cat}
            className={
              "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors " +
              (category === cat
                ? "border-transparent bg-sky-600 text-white shadow-sm"
                : "border-[color:var(--app-input-border)] bg-[color:var(--app-card-bg)] text-[color:var(--app-text)] hover:bg-[color:var(--app-hover)]")
            }
          >
            {cat}
          </button>
        ))}
      </div>

      <p ref={listRef} className="app-muted mt-5 scroll-mt-24 px-1 text-sm">
        {results.length} {results.length === 1 ? "answer" : "answers"}
        {query && ` for “${query}”`}
        {totalPages > 1 && ` · page ${safePage} of ${totalPages}`}
      </p>

      <div className="app-card mt-2 overflow-hidden rounded-2xl border">
        {pageItems.map((faq) => (
          <FaqItem
            key={faq.id}
            faq={faq}
            terms={terms}
            isOpen={isOpen(faq.id)}
            isLinked={faq.id === targetId}
            onToggle={() => toggle(faq.id)}
            onCopyLink={() => copyLink(faq.id)}
            copied={copiedId === faq.id}
          />
        ))}

        {!results.length && (
          <EmptyState
            icon={SearchOffRoundedIcon}
            title={`No answers matched “${query}”.`}
            description="Try a shorter search, or send it to HelpDesk and we'll add it here."
            action={
              <a
                href={`mailto:helpdesk@bigclumber.com?subject=Node%20Web%20Help%20Desk%20Ticket%20&body=${encodeURIComponent(
                  `I searched the Node FAQ for "${query}" and couldn't find an answer.`
                )}`}
                className="app-btn app-btn-primary"
              >
                Ask HelpDesk
              </a>
            }
          />
        )}
      </div>

      {totalPages > 1 && (
        <nav
          aria-label="FAQ pages"
          className="mt-5 flex flex-wrap items-center justify-center gap-1.5"
        >
          <button
            type="button"
            onClick={() => goToPage(safePage - 1)}
            disabled={safePage === 1}
            className="app-btn app-btn-secondary app-btn-sm"
          >
            <ChevronLeftIcon fontSize="small" />
            Prev
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => goToPage(n)}
              aria-current={n === safePage ? "page" : undefined}
              className={
                "h-9 w-9 rounded-full text-sm font-semibold tabular-nums transition-colors " +
                (n === safePage
                  ? "bg-sky-600 text-white shadow-sm"
                  : "text-[color:var(--app-text)] hover:bg-[color:var(--app-hover)]")
              }
            >
              {n}
            </button>
          ))}

          <button
            type="button"
            onClick={() => goToPage(safePage + 1)}
            disabled={safePage === totalPages}
            className="app-btn app-btn-secondary app-btn-sm"
          >
            Next
            <ChevronRightIcon fontSize="small" />
          </button>
        </nav>
      )}
    </div>
  );
};

export default FaqSearch;
